import React from 'react';
import {
  Button,
  Card,
  HeadingText,
  ParagraphText,
  queryHelpers,
  StackItem,
  StackLayout,
  useMediaQuery,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useMasonryEngineActionEmitter } from '@leagueplatform/masonry-engine';
import { InternalOrExternalLink } from '@leagueplatform/web-common-components';
import {
  MasonryCTACardRendererProps,
  MasonryDeeplinkAction,
} from 'common/masonry-types';
import { getButtonIconToken } from 'utils/masonry-cta-card/icon-mapping';
import { BodyTextHtmlTagOptions } from 'utils/masonry-cta-card/body-text-html-tag-options';
import { useDfdFlowModalLauncher } from 'hooks/use-dfd-flow-modal-launcher.hook';
import { SquareImageContainer } from './common/square-image-container.component';
import { DismissButton } from './common/dismiss-button';

export const MasonryCTACardRenderer = ({
  body,
  buttonIndicator,
  buttonLabel,
  eyebrow,
  heading,
  image,
  imageAltText = '',
  isChildItem = false,
  onButtonClick,
  onDismiss,
  showMobile = false,
  rootElementRef,
  styles,
}: MasonryCTACardRendererProps) => {
  const isMobile = useMediaQuery(queryHelpers.only('mobile'));
  const showSmallerCard = isMobile || isChildItem || showMobile;

  const invokeAction = useMasonryEngineActionEmitter();
  const openFlowModal = useDfdFlowModalLauncher();

  const buttonHref = (onButtonClick as MasonryDeeplinkAction)?.payload.url;

  const handleButtonClick = (event: React.MouseEvent) => {
    if (openFlowModal(buttonHref)) {
      event.preventDefault();
      return;
    }

    if (onButtonClick) {
      invokeAction({
        event,
        ...onButtonClick,
      });
    }
  };

  return (
    <Card.Elevated
      css={{
        backgroundColor: '$surfaceBackgroundTertiary',
        padding: '$oneAndHalf',
        ...styles,
      }}
      ref={rootElementRef}
    >
      {onDismiss && (
        <StackItem horizontalAlignment="end">
          <DismissButton onDismiss={onDismiss} />
        </StackItem>
      )}
      <StackLayout
        orientation="horizontal"
        verticalAlignment="center"
        spacing={{
          '@mobile': '$threeQuarters',
          '@initial': showSmallerCard ? '$threeQuarters' : '$oneAndHalf',
        }}
      >
        <StackLayout orientation="vertical" spacing="$quarter">
          {eyebrow && <UtilityText size="eyebrow">{eyebrow}</UtilityText>}
          <HeadingText level="3" size="sm">
            {heading}
          </HeadingText>
          <ParagraphText size="sm" htmlStringOptions={BodyTextHtmlTagOptions}>
            {body}
          </ParagraphText>

          {!showSmallerCard && (
            <Button
              indicator={
                buttonIndicator
                  ? getButtonIconToken(buttonIndicator)
                  : undefined
              }
              css={{ padding: '$half $two', marginTop: '$one' }}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...(buttonHref && {
                as: InternalOrExternalLink,
                disableDefaultOnClick: true,
              })}
              href={buttonHref}
              onClick={handleButtonClick}
            >
              {buttonLabel}
            </Button>
          )}
        </StackLayout>
        {image && (
          <SquareImageContainer
            src={image}
            alt={imageAltText}
            maxWidth="8rem"
            maxWidthDesktop={isChildItem ? '8rem' : '11.2rem'}
          />
        )}
      </StackLayout>
      {showSmallerCard && (
        <Button
          width="fillContainer"
          indicator={
            buttonIndicator ? getButtonIconToken(buttonIndicator) : undefined
          }
          css={{ padding: '$half $two', marginTop: '$one' }}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(buttonHref && {
            as: InternalOrExternalLink,
            disableDefaultOnClick: true,
          })}
          href={buttonHref}
          onClick={handleButtonClick}
        >
          {buttonLabel}
        </Button>
      )}
    </Card.Elevated>
  );
};
